/**
 * Y-axis ticks for the historical Recharts price chart (1–2–5 “nice” steps).
 * Steps can be sub-dollar when the visible span is small (zoomed view).
 */

export type HistoricalChartPriceMode = 'candle' | 'line';

function toFiniteNumber(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = parseFloat(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

/**
 * Min/max price actually reached in the visible window (same semantics as domain extrema, without padding).
 */
export function visiblePriceMinMaxFromHistoricalRows(
  chartData: unknown[],
  mode: HistoricalChartPriceMode,
): { min: number; max: number } {
  if (!chartData.length) return { min: 0, max: 1 };

  if (mode === 'candle') {
    const lows: number[] = [];
    const highs: number[] = [];
    for (const d of chartData as Record<string, unknown>[]) {
      const c = toFiniteNumber(d.close, toFiniteNumber(d.price, NaN));
      if (!Number.isFinite(c)) continue;
      const o = toFiniteNumber(d.open, c);
      const lowRaw = toFiniteNumber(d.low, NaN);
      const sessionLow = Number.isFinite(lowRaw) ? lowRaw : Math.min(o, c);
      const highRaw = toFiniteNumber(d.high, NaN);
      const sessionHigh = Number.isFinite(highRaw) ? highRaw : Math.max(o, c);
      lows.push(Math.min(sessionLow, o, c));
      highs.push(Math.max(sessionHigh, o, c));
    }
    if (!lows.length || !highs.length) return { min: 0, max: 1 };
    return { min: Math.min(...lows), max: Math.max(...highs) };
  }

  const prices = (chartData as Record<string, unknown>[])
    .map((d) => d.price)
    .filter((p): p is number => typeof p === 'number' && Number.isFinite(p));
  if (!prices.length) return { min: 0, max: 1 };
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

/** 1–2–5 × 10^n step, no artificial $1 floor (allows 0.5, 0.25, …). */
function nicePriceStep(raw: number): number {
  const r = Math.max(raw, 1e-12);
  const exp = Math.floor(Math.log10(r));
  const pow10 = 10 ** exp;
  const m = r / pow10;
  const mult = m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10;
  return mult * pow10;
}

/** Decimal places so `start + i * step` can be rounded without drift (handles 0.25, 0.5, …). */
function decimalsForPriceStep(step: number): number {
  if (!Number.isFinite(step) || step <= 0) return 2;
  let x = step;
  for (let d = 0; d <= 8; d++) {
    const nearest = Math.round(x);
    if (Math.abs(x - nearest) < 1e-9 * Math.max(1, nearest)) {
      return d;
    }
    x *= 10;
  }
  return 8;
}

function roundToStepGrid(x: number, step: number, decimals: number): number {
  if (decimals <= 0) {
    return Math.round(x / step) * step;
  }
  const f = 10 ** decimals;
  return Math.round(x * f) / f;
}

export type PriceAxisTicksResult = {
  ticks: number[];
  /** Actual grid step used (for label formatting). */
  step: number;
};

/**
 * Strictly increasing tick values covering `[dataMin, dataMax]` on a nice price grid.
 * Default aims for ~12–14 labels so spans like $0–$15 use $1 steps instead of $5.
 */
export function buildWholeDollarYAxisTicks(
  dataMin: number,
  dataMax: number,
  options?: { targetTickCount?: number },
): PriceAxisTicksResult {
  const target = options?.targetTickCount ?? 14;
  const lo = Math.max(0, Math.min(dataMin, dataMax));
  const hi = Math.max(lo, dataMax);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
    return { ticks: [0, 1], step: 1 };
  }

  const span = hi - lo || 1;
  const rawDesired = span / Math.max(2, target - 1);
  const step = Math.max(1e-12, nicePriceStep(rawDesired));
  const decimals = decimalsForPriceStep(step);

  let start = Math.floor(lo / step) * step;
  let end = Math.ceil(hi / step) * step;
  if (start < 0) start = 0;
  if (end < start) end = start + step;

  start = roundToStepGrid(start, step, decimals);
  end = roundToStepGrid(end, step, decimals);

  const ticks: number[] = [];
  const maxTicks = 60;
  const n = Math.min(maxTicks, Math.ceil((end - start) / step) + 3);
  for (let i = 0; i <= n; i++) {
    const t = roundToStepGrid(start + i * step, step, decimals);
    if (t < lo - step * 1e-6) continue;
    if (t > hi + step * 1e-6) break;
    if (ticks.length === 0 || Math.abs(t - ticks[ticks.length - 1]) > step * 1e-9) {
      ticks.push(t);
    }
  }

  if (!ticks.length) {
    return { ticks: [lo, hi], step: span };
  }
  return { ticks, step };
}

/** Format a Y tick; pass `step` from {@link buildWholeDollarYAxisTicks} for correct decimals. */
export function formatPriceAxisTick(value: unknown, step: number): string {
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  if (!Number.isFinite(n)) return '';
  const s = Math.abs(step);
  let decimals = 0;
  if (s >= 1) {
    decimals = 0;
  } else if (s >= 0.1) {
    decimals = 1;
  } else if (s >= 0.01) {
    decimals = 2;
  } else {
    decimals = Math.min(4, Math.max(2, decimalsForPriceStep(s)));
  }

  const formatted =
    decimals === 0
      ? Math.round(n).toLocaleString('en-US')
      : n.toLocaleString('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
  return `$${formatted}`;
}

/** @deprecated Prefer {@link formatPriceAxisTick} with the step returned from {@link buildWholeDollarYAxisTicks}. */
export function formatWholeDollarAxisTick(value: unknown): string {
  return formatPriceAxisTick(value, 1);
}
